/* Sets --header-height and --header-group-height: theme.liquid only sets those for a Horizon <header-component>. */
(() => {
  const DESKTOP = () => window.matchMedia('(min-width: 750px)').matches;
  const SOLID_THRESHOLD = 8;

  /* Horizon >=990px: `.page-wrapper` is the scroll container, so scroll events never reach `document`. */
  const SQUEEZE = window.matchMedia('(min-width: 990px)');
  const pageWrapper = () => (SQUEEZE.matches ? document.querySelector('.page-wrapper') : null);
  const scrollTopOf = () =>
    (pageWrapper() ?? document.scrollingElement ?? document.documentElement).scrollTop;
  const scrollEventTarget = () => pageWrapper() ?? document;

  const init = (header) => {
    if (header.dataset.bronteHeaderReady) return;
    header.dataset.bronteHeaderReady = 'true';

    const panelItems = [...header.querySelectorAll('[data-panel-trigger]')];
    let openItem = null;
    let panelCloseTimer = null;

    const syncPanelFlag = () => {
      if (openItem) header.setAttribute('data-panel-open', '');
      else header.removeAttribute('data-panel-open');
    };

    const closePanel = (item) => {
      if (!item) return;
      item.removeAttribute('data-open');
      item.querySelector('.bronte-header__nav-link')?.setAttribute('aria-expanded', 'false');
      if (openItem === item) openItem = null;
      syncPanelFlag();
    };

    const openPanel = (item) => {
      if (openItem && openItem !== item) closePanel(openItem);
      item.setAttribute('data-open', '');
      item.querySelector('.bronte-header__nav-link')?.setAttribute('aria-expanded', 'true');
      openItem = item;
      syncPanelFlag();
    };

    const cancelPanelClose = () => {
      if (panelCloseTimer) {
        window.clearTimeout(panelCloseTimer);
        panelCloseTimer = null;
      }
    };

    const schedulePanelClose = (item) => {
      cancelPanelClose();
      panelCloseTimer = window.setTimeout(() => closePanel(item), 220);
    };

    panelItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (DESKTOP()) {
          cancelPanelClose();
          openPanel(item);
        }
      });
      item.addEventListener('mouseleave', () => {
        if (DESKTOP()) schedulePanelClose(item);
      });
      item.addEventListener('focusin', () => {
        if (DESKTOP()) {
          cancelPanelClose();
          openPanel(item);
        }
      });
      item.addEventListener('focusout', (event) => {
        if (DESKTOP() && !item.contains(event.relatedTarget)) schedulePanelClose(item);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openItem) closePanel(openItem);
    });

    // Horizon's drawer marks its <details> with .menu-open while sliding in.
    const drawerOpen = () => !!header.querySelector('.menu-drawer-container.menu-open');
    const overlayOpen = () => drawerOpen() || openItem != null;

    let lastScroll = scrollTopOf();
    let rafId = null;

    const updateScroll = () => {
      rafId = null;
      const scrollTop = scrollTopOf();
      const scrollingUp = scrollTop < lastScroll;
      const pastTop = scrollTop > SOLID_THRESHOLD;

      header.dataset.stickyState = pastTop ? 'active' : 'inactive';

      // Never transform while an overlay is open: a transformed header becomes the containing block for the fixed drawer.
      if (overlayOpen()) {
        header.dataset.scrollDirection = 'none';
        lastScroll = scrollTop;
        return;
      }

      header.dataset.scrollDirection = pastTop ? (scrollingUp ? 'up' : 'down') : 'none';
      lastScroll = scrollTop;
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateScroll);
    };

    let scrollTarget = scrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    updateScroll();

    SQUEEZE.addEventListener('change', () => {
      scrollTarget.removeEventListener('scroll', onScroll);
      scrollTarget = scrollEventTarget();
      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
      lastScroll = scrollTopOf();
      updateScroll();
    });

    const bar = header.querySelector('.bronte-header__bar');
    const group = document.getElementById('header-group');
    const isTransparent = header.classList.contains('bronte-header--transparent');
    const sectionEl = header.closest('.shopify-section');

    const setHeights = () => {
      const barHeight = Math.round((bar || header).offsetHeight);
      document.body.style.setProperty('--header-height', `${barHeight}px`);

      let groupHeight = 0;
      if (group) {
        [...group.children].forEach((child) => {
          if (child !== sectionEl) groupHeight += child.offsetHeight || 0;
        });
        if (isTransparent && sectionEl?.nextElementSibling) groupHeight += barHeight;
      }
      document.body.style.setProperty('--header-group-height', `${groupHeight || barHeight}px`);
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    };

    setHeights();
    if ('ResizeObserver' in window) new ResizeObserver(setHeights).observe(bar || header);

    window.addEventListener('resize', () => {
      if (!DESKTOP() && openItem) closePanel(openItem);
      setHeights();
    });
  };

  const boot = () => document.querySelectorAll('[data-bronte-header]').forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (event) => {
    const header = event.target.querySelector('[data-bronte-header]');
    if (header) init(header);
  });
})();
